import type { NiceModalHandler } from '@ebay/nice-modal-react'
import NiceModal, { useModal as useNiceModal } from '@ebay/nice-modal-react'
import type { JSX } from 'react'

/**
 * Extended modal handler with typed callOrReturn method that also removes the modal
 */
export interface TypedModalHandler<TResult = void> extends NiceModalHandler {
  /**
   * Resolves the modal with a value and immediately removes it from the DOM
   * @param value - The value to callOrReturn with
   */
  resolveRemove: (value: TResult) => void

  rejectRemove: () => void
}

/**
 * A typed modal component that includes a type-safe `show` method and `useModal` hook.
 * It IS the React component (a function), with `show` and `useModal` attached as properties.
 *
 * @template TProps - The props type for the modal component
 * @template TResult - The result type returned by the modal
 * @template TShow - Custom show method signature (optional, for generic modals)
 */
export type TypedModal<
  TProps = void,
  TResult = void,
  TShow = (props: TProps) => Promise<TResult>,
> = React.FC<TProps> & {
  /**
   * Type-safe show method that infers props and result types.
   * For modals with generic props, use the TShow parameter to provide a custom signature.
   */
  show: TShow

  /**
   * Typed useModal hook for this specific modal
   * Returns an extended modal handler with typed callOrReturn methods
   */
  useModal: () => TypedModalHandler<TResult>
}

/**
 * Creates a type-safe modal wrapper around NiceModal.create
 *
 * This factory function solves the type inference problem with nice-modal-react
 * by encoding the Props and Result types at the modal definition site rather than
 * at each call site.
 *
 * @example
 * ```tsx
 * // Define the modal with types
 * export interface MyModalProps {
 *   userId: string
 *   mode: 'edit' | 'view'
 * }
 *
 * export interface MyModalResult {
 *   saved: boolean
 *   data: User
 * }
 *
 * export const MyModal = createTypedModal<MyModalProps, MyModalResult>((props) => {
 *   const modal = MyModal.useModal()  // Typed hook specific to this modal
 *
 *   const handleSave = (user: User) => {
 *     // Option 1: callOrReturn and remove in one call
 *     modal.resolveRemove({ saved: true, data: user })
 *   }
 *
 *   const handleCancel = () => {
 *     // Option 2: traditional separate calls
 *     modal.callOrReturn({ saved: false, data: null })
 *     modal.hide()
 *   }
 *
 *   return <div>...</div>
 * })
 *
 * // Use the modal with full type inference
 * const result = await MyModal.show({ userId: '123', mode: 'edit' })
 * // result is typed as MyModalResult ✓
 * ```
 *
 * @example
 * ```tsx
 * // Generic modal with dependent types
 * interface SearchProps<T extends TableName> {
 *   entityType: T
 * }
 *
 * interface SearchResult<T extends TableName> {
 *   value: Entity[T]
 * }
 *
 * export const SearchModal = createTypedModal<
 *   SearchProps<TableName>,
 *   SearchResult<TableName>,
 *   <T extends TableName>(props: SearchProps<T>) => Promise<SearchResult<T>>
 * >((props) => { ... })
 *
 * // Usage - generic T is inferred from the literal value
 * const result = await SearchModal.show({ entityType: 'pharmacy' })
 * // result is typed as SearchResult<'pharmacy'> ✓
 * ```
 *
 * @template TProps - The props type for the modal component
 * @template TResult - The type of value the modal resolves with (defaults to void)
 * @template TShow - Custom show signature for modals with generic props (optional)
 * @param component - The modal component function
 * @returns A TypedModal object with a type-safe `show` method and `useModal` hook
 */
export type EditMode = 'create' | 'edit'

export interface EditModalProps<TEntity, TSection extends string = string, TId extends string = string> {
  id?: TId
  section?: TSection
  defaults?: Partial<TEntity>
}

export interface EditModalResult<TEntity> {
  value: TEntity
  mode: EditMode
}

export type EditModalShow<TEntity, TSection extends string = string, TId extends string = string> = (
  props?: EditModalProps<TEntity, TSection, TId>,
) => Promise<EditModalResult<TEntity>>

export type EditModal<TEntity, TSection extends string = string, TId extends string = string> = TypedModal<
  EditModalProps<TEntity, TSection, TId> | undefined,
  EditModalResult<TEntity>,
  EditModalShow<TEntity, TSection, TId>
>

export function createTypedModal<TProps = void, TResult = void, TShow = (props: TProps) => Promise<TResult>>(
  component: (props: TProps) => JSX.Element,
): TypedModal<TProps, TResult, TShow> {
  const NiceModalComponent = NiceModal.create(component as never)

  const modal = NiceModalComponent as unknown as TypedModal<TProps, TResult, TShow>

  modal.show = ((props: TProps) => {
    return NiceModal.show<never, never, never>(NiceModalComponent, props as never)
  }) as TShow

  modal.useModal = useAppNiceModal

  return modal
}

export function useAppNiceModal<TResult>() {
  const m = useNiceModal()
  return {
    ...m,
    resolveRemove: (value: TResult) => {
      m.resolve(value)
      m.remove()
    },
    rejectRemove: () => {
      m.reject()
      m.remove()
    },
  }
}
